import React from 'react';
import { ArrowRightIcon } from 'lucide-react';
import { usePortal } from '../../contexts/PortalContext';
import { CopyClientLink } from './CopyClientLink';
import { CopyShowLink } from './CopyShowLink';
import { projectStatus } from '../../utils/status';
import type { Project } from '../../types/portal';

/** "Changes" outranks "With client," which outranks the plain derived status. */
export function rowStatusLabel(row: Project): string {
  const waiting = row.deliverables.filter(item => item.status === 'Ready for Review' && item.visibleToClient).length;
  const changes = row.deliverables.reduce((total, item) => total + item.changeRequests.filter(request => !request.resolved).length, 0);
  return changes > 0 ? 'Changes' : waiting > 0 ? 'With client' : projectStatus(row);
}

/**
 * THE SHAPE OF A ROW. 16 Oct 2026.
 *
 * For a year the client name was the headline, set at text-4xl, with the
 * project in a smaller grey beside it. Three things were wrong with that,
 * and they are the same three on Wrap-ups and Call Sheets:
 *
 *   1. **The wrong thing was loudest.** You click the project. You tell the
 *      rows apart by the project. The client is context — and on most rows
 *      it is context you already knew, because you came here looking for it.
 *   2. **Row weight was decided by name length.** ARABIAN TRAVEL MARKET set
 *      at 36px is a banner; WFES at 36px is a murmur. The eye reads size as
 *      importance, so the client with the longest name always looked like
 *      the most important one in the studio.
 *   3. **Status moved between screens.** Here it sat under the name; on
 *      Wrap-ups it sat at the right edge. Same fact, two homes, so neither
 *      could be scanned down a column.
 *
 * So: the client becomes an eyebrow in the label register — which has no
 * size to vary — the project takes the display size, and the status sits at
 * the right edge on every list in the studio. The name is also a notch
 * smaller than the old client name was (28px, not 36px), because the point
 * of the change is to calm the column, not to move the shouting.
 */
function ProjectRow({
  row,
  openProject,
  showClient = true,
  showCopyLink = true
}: {
  row: Project;
  openProject: (id: string) => void;
  /** False inside a show group, where the header above already names the client. */
  showClient?: boolean;
  /** False inside a multi-episode show group — the show link above is the
      only link that episode should ever be reached by. */
  showCopyLink?: boolean;
}) {
  return <div className="grid grid-cols-1 items-baseline gap-x-8 gap-y-3 sm:grid-cols-12" data-id="element-1210" data-mp="32tq00zmeo78k">
      <button type="button" onClick={() => openProject(row.id)} className={['group grid grid-cols-1 items-baseline gap-x-8 gap-y-3 py-8 text-left sm:grid-cols-10', showCopyLink ? 'sm:col-span-10' : 'sm:col-span-12'].join(' ')} data-id="element-1211" data-mp="2xz3ngt4l88mw">
        <span className="min-w-0 sm:col-span-7" data-id="element-1212" data-mp="3loxzx9kzvv6m">
          {showClient && <span className="label block text-paper/50" data-id="element-1213" data-mp="188ehv94remca">{row.client}</span>}
          <span className="mt-2.5 block font-display text-2xl leading-tight sm:text-[1.75rem]" data-id="element-1214" data-mp="1d6y2n67m9s32">
            {row.name}
          </span>
        </span>
        <span className="label flex items-center gap-4 text-paper sm:col-span-3 sm:justify-end" data-id="element-1215" data-mp="1h7rrq2wspcxj">
          {rowStatusLabel(row)}
          <ArrowRightIcon className="h-3.5 w-3.5 shrink-0 opacity-40 transition-transform duration-150 ease-out group-hover:translate-x-1 group-hover:opacity-100" strokeWidth={1.25} data-id="element-1216" data-mp="1m5ji2shskqzm" />
        </span>
      </button>
      {showCopyLink && <span className="pb-8 sm:col-span-2 sm:justify-self-end sm:pt-8" data-id="element-1217" data-mp="20xtshxsiid7o">
          <CopyClientLink slug={row.slug} projectId={row.id} data-id="element-1218" data-mp="24ydo2wxft8rp" />
        </span>}
    </div>;
}

/**
 * Active projects, grouped under the show they belong to so a multi-episode
 * series reads as one entry with its own client link rather than a long flat
 * list. A project with no show is unaffected.
 *
 * Shows with zero projects can't occur here — the database drops a show the
 * moment its last project is removed or reassigned — so nothing stale ever
 * lingers in this list.
 *
 * Full width since 15 Sep 2026: Recent Activity used to take the right third
 * of this row. See Projects.tsx.
 *
 * **The section is headed "Projects", not "Active Projects" (16 Oct 2026).**
 * It was the second: a project can be marked Complete without being
 * archived, so the list routinely showed a row reading COMPLETE directly
 * under a heading saying everything below it was active. The heading was the
 * part that was wrong — the archive has its own button underneath.
 */
export function ProjectList({
  onShowArchive
}: {
  onShowArchive: () => void;
}) {
  const {
    activeProjects,
    archivedProjects,
    shows,
    openProject,
    openShow
  } = usePortal();
  const projectsByShow = new Map<string, Project[]>();
  for (const row of activeProjects) {
    if (!row.showId) continue;
    const list = projectsByShow.get(row.showId) ?? [];
    list.push(row);
    projectsByShow.set(row.showId, list);
  }
  const showGroups = shows.map(show => ({
    show,
    rows: projectsByShow.get(show.id) ?? []
  })).filter(group => group.rows.length > 0);
  const standaloneProjects = activeProjects.filter(row => !row.showId);

  /* One counter across both lists, so the stagger runs down the page rather
     than restarting halfway. Capped so the last row of a long list is not
     left waiting half a second for its turn. */
  let rowIndex = -1;
  const nextRow = () => {
    rowIndex += 1;
    return {
      ['--row' as string]: Math.min(rowIndex, 10)
    };
  };
  return <section aria-labelledby="active-projects" className="mt-12" data-id="element-1219" data-mp="3g4kvd1lwet87">
      <div className="flex flex-wrap items-center justify-between gap-4 border-b border-ruleDark pb-4" data-id="element-1220" data-mp="3k77hqmzsvly0">
        <h2 id="active-projects" className="label m-0 text-paper/40" data-id="element-1221" data-mp="brnqtumlqfm4">
          Projects
        </h2>
      </div>

      <ul className="m-0 mt-4 list-none p-0" data-id="element-1222" data-mp="gkguef6c3hwm">
        {showGroups.map(({
        show,
        rows
      }) => <li key={show.id} className="row-in border-b border-ruleDark" style={nextRow()} data-id="element-1223" data-mp="qemh3m9q33z9">
            <div className="grid grid-cols-1 items-baseline gap-x-8 gap-y-3 sm:grid-cols-12" data-id="element-1224" data-mp="mevpjgcdgjto">
              {/* One episode reads as a single row: the client leads, the
                  show's name sits where a standalone project's would, and no
                  "Show" label is needed to explain it. Several episodes keep
                  the same shape and open the Show Admin page instead. */}
              <button type="button" onClick={() => rows.length === 1 ? openProject(rows[0].id) : openShow(show.id)} className="group grid grid-cols-1 items-baseline gap-x-8 gap-y-2 py-8 text-left sm:col-span-10 sm:grid-cols-10" data-id="element-1225" data-mp="2o0j6ntfn02z">
                <span className="min-w-0 sm:col-span-7" data-id="element-1226" data-mp="3ktvet4y52jmt">
                  <span className="label block text-paper/50" data-id="element-1227" data-mp="3fwdf8560hgrh">{show.client}</span>
                  <span className="mt-2.5 block font-display text-2xl leading-tight sm:text-[1.75rem]" data-id="element-1228" data-mp="3fyg3ajnlsztk">
                    {show.name}
                  </span>
                  {rows.length > 1 && <span className="label-sm mt-2 block text-paper/30" data-id="element-1229" data-mp="3fqcq3dnnd6wn">{rows.length} episodes</span>}
                </span>
                <span className="label flex items-center gap-4 text-paper sm:col-span-3 sm:justify-end" data-id="element-1230" data-mp="3koe9jzozj88n">
                  {rows.length === 1 ? rowStatusLabel(rows[0]) : 'View Episodes'}
                  <ArrowRightIcon className="h-3.5 w-3.5 shrink-0 opacity-40 transition-transform duration-150 ease-out group-hover:translate-x-1 group-hover:opacity-100" strokeWidth={1.25} data-id="element-1231" data-mp="2rzyxnm15h61j" />
                </span>
              </button>
              <span className="pb-8 sm:col-span-2 sm:justify-self-end sm:pt-8" data-id="element-1232" data-mp="24bz6ikzxfdmf">
                <CopyShowLink slug={show.slug} showId={show.id} data-id="element-1233" data-mp="29a0o11hpf4vs" />
              </span>
            </div>
          </li>)}

        {standaloneProjects.map(row => <li key={row.id} className="row-in border-b border-ruleDark" style={nextRow()} data-id="element-1234" data-mp="3nitl21nzn3dx">
            <ProjectRow row={row} openProject={openProject} data-id="element-1235" data-mp="3jgym33tj523b" />
          </li>)}

        {activeProjects.length === 0 && <li className="border-b border-ruleDark py-8 font-display text-xl text-paper/40" data-id="element-1236" data-mp="3sg3gpz1z0ba7">
            Nothing in production.
          </li>}
      </ul>

      {archivedProjects.length > 0 && <button type="button" onClick={onShowArchive} className="label-sm mt-10 text-paper/35 underline underline-offset-4 transition-colors duration-150 ease-out hover:text-paper/70" data-id="element-1237" data-mp="68veh5mckkie">
          Archived Projects · {archivedProjects.length}
        </button>}
    </section>;
}